import { create } from "zustand";
import { searchNotifications, markNotificationsAsRead, } from "@/shared/api/notifications";
import { wsManager } from "@/shared/lib/websocket";
import { isPageActive } from "@/shared/lib/pageVisibility";
import { toast } from "@/shared/ui/Toast/toast";
import { describeNotification, isHiddenNotification } from "./describe";
import { resolveNotificationHref, hasNotificationTarget } from "./resolveHref";
// Сколько последних уведомлений держим в центре.
const CENTER_LIMIT = 30;
// Дебаунс refresh при бурсте live-событий.
const LIVE_REFRESH_DEBOUNCE_MS = 400;
// id уведомлений, уже «увиденных» этим сеансом (стартовый бэклог + то, что
// пользователь видел в панели + уже стостированное). Нужен, чтобы live-тост
// показывался ТОЛЬКО для действительно новых непрочитанных уведомлений и не
// заваливал экран бэклогом. Держим вне zustand — это не рендерится.
const seenIds = new Set();
const seedSeen = (items) => {
    for (const n of items)
        seenIds.add(n.id);
};
// Язык для текста тоста читаем из localStorage (мост живёт вне React-дерева,
// контекст языка тут недоступен). Ключ — тот же, что у LanguageProvider.
const readLanguage = () => {
    try {
        return localStorage.getItem("language") === "en" ? "en" : "ru";
    }
    catch {
        return "ru";
    }
};
// Навигатор из React-дерева (регистрирует NotificationBell через useNavigate).
// Тост и центр живут вне контекста роутера, поэтому SPA-переход делаем через
// зарегистрированный navigate; фолбэк — полная навигация браузера.
let routerNavigate = null;
export const setNotificationNavigator = (fn) => {
    routerNavigate = fn;
};
const go = (to) => {
    if (routerNavigate)
        routerNavigate(to);
    else
        window.location.assign(to);
};
export const useNotificationCenter = create((set, get) => ({
    items: [],
    unreadCount: 0,
    total: 0,
    isOpen: false,
    isLoading: false,
    isLoadingMore: false,
    isLoaded: false,
    load: async () => {
        if (get().isLoading)
            return;
        set({ isLoading: true });
        try {
            // Список (все) + счётчик непрочитанного отдельным запросом (total).
            const [list, unread] = await Promise.all([
                searchNotifications({ limit: CENTER_LIMIT }),
                searchNotifications({ limit: 1, isRead: false }),
            ]);
            set({
                items: list.results,
                unreadCount: unread.total,
                total: list.total,
                isLoaded: true,
                isLoading: false,
            });
            // Стартовый список — это бэклог: помечаем «увиденным», чтобы он не
            // прилетел тостами при первом live-refresh.
            seedSeen(list.results);
            // Скрытые generic-«пустышки» не должны висеть в бейдже без видимого
            // уведомления — гасим их сразу (см. suppressHiddenUnread).
            void suppressHiddenUnread();
        }
        catch (e) {
            console.error("Failed to load notifications:", e);
            set({ isLoading: false });
        }
    },
    refresh: async () => {
        try {
            // Каноничный рефреш первой «страницы», но не уже уже, чем догруженный
            // объём — иначе live-рефреш затирал бы подгруженные пагинацией страницы.
            const limit = Math.max(CENTER_LIMIT, get().items.length);
            const [list, unread] = await Promise.all([
                searchNotifications({ limit }),
                searchNotifications({ limit: 1, isRead: false }),
            ]);
            set({
                items: list.results,
                unreadCount: unread.total,
                total: list.total,
                isLoaded: true,
            });
        }
        catch (e) {
            console.error("Failed to refresh notifications:", e);
        }
    },
    loadMore: async () => {
        const { isLoadingMore, items, total } = get();
        if (isLoadingMore || items.length >= total)
            return;
        set({ isLoadingMore: true });
        try {
            const page = await searchNotifications({
                limit: CENTER_LIMIT,
                offset: items.length,
            });
            // Аппенд без дублей по id (страницы могли сдвинуться live-событием).
            const existing = new Set(get().items.map((n) => n.id));
            const appended = page.results.filter((n) => !existing.has(n.id));
            set((s) => ({
                items: [...s.items, ...appended],
                total: page.total,
                isLoadingMore: false,
            }));
            // Догруженное — это бэклог: помечаем «увиденным», чтобы live-refresh
            // не стостировал старые непрочитанные из глубины истории.
            seedSeen(appended);
        }
        catch (e) {
            console.error("Failed to load more notifications:", e);
            set({ isLoadingMore: false });
        }
    },
    open: () => {
        set({ isOpen: true });
        // Подтягиваем канонический список при открытии (появились live-события /
        // изменился is_read в другой вкладке). Всё показанное в панели помечаем
        // «увиденным» — иначе последующий live-refresh стостировал бы то, что
        // пользователь уже видел открытым.
        void (async () => {
            await get().refresh();
            seedSeen(get().items);
        })();
    },
    close: () => set({ isOpen: false }),
    toggle: () => (get().isOpen ? get().close() : get().open()),
    markAllRead: async () => {
        // Собираем ВСЕ непрочитанные id (не только видимые в центре).
        let ids = [];
        try {
            const unread = await searchNotifications({ limit: 100, isRead: false });
            ids = unread.results.map((n) => n.id);
        }
        catch (e) {
            console.error("Failed to fetch unread notifications:", e);
            // fallback — по крайней мере видимые непрочитанные.
            ids = get().items.filter((n) => !n.is_read).map((n) => n.id);
        }
        if (ids.length === 0) {
            set({ unreadCount: 0 });
            return;
        }
        try {
            await markNotificationsAsRead(ids);
        }
        catch (e) {
            console.error("Failed to mark notifications as read:", e);
            return;
        }
        const idSet = new Set(ids);
        set((s) => ({
            items: s.items.map((n) => idSet.has(n.id) ? { ...n, is_read: true } : n),
            unreadCount: 0,
        }));
    },
    markOne: async (id) => {
        const item = get().items.find((n) => n.id === id);
        if (!item || item.is_read)
            return;
        // Оптимистично помечаем локально (мгновенный маркер прочитанного), затем
        // подтверждаем на сервере; при ошибке откатываем.
        set((s) => ({
            items: s.items.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
            unreadCount: Math.max(0, s.unreadCount - 1),
        }));
        try {
            await markNotificationsAsRead([id]);
        }
        catch (e) {
            console.error("Failed to mark notification as read:", e);
            set((s) => ({
                items: s.items.map((n) => (n.id === id ? { ...n, is_read: false } : n)),
                unreadCount: s.unreadCount + 1,
            }));
        }
    },
    openNotification: async (n) => {
        // Закрываем панель сразу (мгновенная реакция). Затем ПАРАЛЛЕЛЬНО помечаем
        // прочитанным (markOne оптимистичен локально + подтверждает на сервере) и
        // резолвим публичный токен. Навигируем ТОЛЬКО после того, как серверная
        // пометка завершилась — иначе refresh на целевой странице мог бы перетереть
        // её непрочитанным состоянием с сервера (уведомление «оживало»).
        get().close();
        const [, href] = await Promise.all([
            get().markOne(n.id),
            resolveNotificationHref(n),
        ]);
        if (href)
            go(href);
    },
    onLive: () => {
        // Мгновенная обратная связь на бейдже + дебаунс-подтягивание каноники и
        // показ тостов новых непрочитанных (счётчик каноника пересчитает).
        set((s) => ({ unreadCount: s.unreadCount + 1 }));
        scheduleLiveRefresh(() => void get().refreshAndToast());
    },
    refreshAndToast: async () => {
        await get().refresh();
        const language = readLanguage();
        // Тостим только НОВЫЕ (не виденные) непрочитанные. is_read берём из
        // каноники REST — бот-уведомления приходят is_read=true и сюда не
        // попадают (и не тостятся, и не считаются непрочитанными). Прочитанные
        // тоже помечаем «увиденными», чтобы не всплывали при следующем refresh.
        const fresh = get().items;
        const toShow = [];
        for (const n of fresh) {
            if (seenIds.has(n.id))
                continue;
            seenIds.add(n.id);
            // Скрытые generic-«пустышки» не тостим (и не считаем видимыми) — их
            // погасит suppressHiddenUnread ниже.
            if (!n.is_read && !isHiddenNotification(n))
                toShow.push(n);
        }
        // Авто-чтение: если вкладка сейчас активна (пользователь на сайте) — новые
        // непрочитанные сразу помечаем прочитанными, чтобы не засорять центр. Тост
        // при этом всё равно показываем (он транзиентный). Свёрнутая вкладка —
        // прежнее поведение: копятся непрочитанными, счётчик растёт.
        if (toShow.length > 0 && isPageActive()) {
            const ids = toShow.map((n) => n.id);
            try {
                await markNotificationsAsRead(ids);
                const idSet = new Set(ids);
                set((s) => ({
                    items: s.items.map((n) => idSet.has(n.id) ? { ...n, is_read: true } : n),
                }));
                // Синхронизируем счётчик с сервером ПОСЛЕ пометки — иначе бейдж
                // «прыгал» бы из-за оптимистичного +1 в onLive.
                await get().refresh();
            }
            catch (e) {
                console.error("Failed to auto-read notifications:", e);
            }
        }
        // Каноника REST отдаёт newest-first — показываем в хронологическом порядке.
        for (const n of toShow.reverse())
            showNotificationToast(n, language);
        // Гасим скрытые generic-непрочитанные (в т.ч. свежие live) — чтобы бейдж
        // не «мигал» цифрой без видимого уведомления.
        void suppressHiddenUnread();
    },
}));
/**
 * Пометить прочитанными скрытые generic-непрочитанные (system / неизвестный тип
 * без текста) среди загруженных. Они не рендерятся в центре и не тостятся, но
 * серверный счётчик их считает — поэтому гасим, чтобы бейдж был консистентен с
 * видимым списком. Локально уменьшаем unreadCount и синхронно PATCH-им сервер.
 */
const suppressHiddenUnread = async () => {
    const store = useNotificationCenter;
    const hidden = store
        .getState()
        .items.filter((n) => !n.is_read && isHiddenNotification(n));
    if (hidden.length === 0)
        return;
    const ids = hidden.map((n) => n.id);
    const idSet = new Set(ids);
    store.setState((s) => ({
        items: s.items.map((n) => idSet.has(n.id) ? { ...n, is_read: true } : n),
        unreadCount: Math.max(0, s.unreadCount - ids.length),
    }));
    try {
        await markNotificationsAsRead(ids);
    }
    catch (e) {
        console.error("Failed to suppress hidden notifications:", e);
    }
};
/**
 * Тост live-уведомления: крестик = пометить прочитанным (PATCH + счётчик);
 * «Перейти» = открыть цель уведомления (хаб песни / прежний маршрут) + пометить
 * прочитанным. Кнопку показываем только когда есть куда вести.
 */
const showNotificationToast = (n, language) => {
    const view = describeNotification(n, language);
    toast.show({
        tone: view.tone,
        title: view.title,
        message: view.message,
        onClose: () => void useNotificationCenter.getState().markOne(n.id),
        action: hasNotificationTarget(n)
            ? {
                label: language === "ru" ? "Перейти" : "Open",
                onAction: () => void useNotificationCenter.getState().openNotification(n),
            }
            : undefined,
    });
};
let liveRefreshTimer = null;
const scheduleLiveRefresh = (fn) => {
    if (liveRefreshTimer)
        clearTimeout(liveRefreshTimer);
    liveRefreshTimer = setTimeout(() => {
        liveRefreshTimer = null;
        fn();
    }, LIVE_REFRESH_DEBOUNCE_MS);
};
// ── WS-мост «live → центр» ────────────────────────────────────────────────
// Подписываемся на ВСЕ уведомления один раз за сессию (idempotent). Backlog
// игнорируем (центр канонично грузится из REST). Для live-события дёргаем
// onLive → каноничный refresh + тост новых непрочитанных (с крестиком-пометкой-
// прочитанным). Тост дедупится по заголовку с доменными тостами страниц, так
// что двойных уведомлений на одном экране нет (см. toastStore.push).
let wsBridgeReady = false;
export const ensureNotificationWsBridge = () => {
    if (wsBridgeReady)
        return;
    wsBridgeReady = true;
    wsManager.subscribeAll((_notification, meta) => {
        if (meta.isBacklog)
            return;
        useNotificationCenter.getState().onLive();
    });
};
